import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { signOut, onAuthStateChanged, signInWithCustomToken } from 'firebase/auth'
import { getAuthInstance } from '../firebase/app'
import api from '../api/api'
import { setUserContext } from '../utils/errorReporting'
import { i18n } from '../i18n/plugin'

function safeLocalStorage(action, key, value) {
  try {
    if (action === 'get') return localStorage.getItem(key)
    if (action === 'set') return localStorage.setItem(key, value)
    if (action === 'remove') return localStorage.removeItem(key)
  } catch (e) {
    console.warn(`localStorage not available (${e.name}):`, e.message)
    return null
  }
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref(null)
  const account = ref(null)
  const storedInviteToken = ref(null) // Store invite token
  const rootSession = ref(null) // Store root admin session during impersonation
  const isLoggedIn = computed(() => !!user.value)
  const isImpersonating = computed(() => !!rootSession.value)
  const isRoot = computed(() => account.value?.isRoot === true)
  const logOut = () => {
    signOut(getAuthInstance())
      .then(() => {
        user.value = null
        account.value = null
        storedInviteToken.value = null // Clear invite token on logout
        rootSession.value = null // Clear impersonation session
        safeLocalStorage('remove', 'rootSession')
        setUserContext(null) // Clear user context for error reporting
      })
      .catch(() => {})
  }

  const setInviteToken = (token) => {
    storedInviteToken.value = token
  }

  const getInviteToken = () => {
    return storedInviteToken.value
  }

  const refreshToken = async () => {
    try {
      if (user.value) {
        const freshToken = await user.value.getIdToken(true)
        // Update the user object with fresh token
        user.value.accessToken = freshToken
        return freshToken
      }
      return null
    } catch (error) {
      console.warn('Failed to refresh Firebase token:', error)
      // Token refresh failed, user needs to re-authenticate
      logOut()
      return null
    }
  }

  const login = async (currentUser, inviteToken) => {
    // Use passed token or stored token
    const tokenToUse = inviteToken || storedInviteToken.value
    // Detect browser language for account creation
    const browserLang = navigator.language || navigator.userLanguage
    try {
      const a = await api.login(currentUser, tokenToUse, browserLang)
      account.value = a
      user.value = currentUser
      // Clear the stored token after use
      storedInviteToken.value = null
      // Set user context for error reporting
      setUserContext({
        uid: currentUser.uid,
        email: currentUser.email
      })
      // Sync i18n locale with account language preference
      if (a?.lang) {
        i18n.global.locale.value = a.lang
      }
      return a
    } catch (e) {
      // Ensure we don't keep a stale firebase session that bypasses guards
      try {
        await signOut(getAuthInstance())
      } catch {
        // Ignore sign-out cleanup failures while recovering from login errors.
      }
      throw e
    }
  }

  const startImpersonation = async (customToken) => {
    // Save current root session (UID only, we'll generate a new token on exit)
    rootSession.value = {
      rootUid: user.value.uid,
      rootEmail: user.value.email,
      rootAccount: { ...account.value }
    }
    safeLocalStorage('set', 'rootSession', JSON.stringify(rootSession.value))

    // Sign in as target user
    const auth = getAuthInstance()
    await signInWithCustomToken(auth, customToken)
    // onAuthStateChanged will update user and account automatically
  }

  const exitImpersonation = async () => {
    const auth = getAuthInstance()
    const saved = rootSession.value || JSON.parse(safeLocalStorage('get', 'rootSession') || 'null')

    if (!saved || !saved.rootUid) {
      console.error('No saved root session found')
      return
    }

    // Request a new custom token for the root user
    const response = await api.rootAdmin.exitImpersonation(saved.rootUid)

    // Sign in with the new custom token
    await signInWithCustomToken(auth, response.customToken)

    // Clear impersonation state
    rootSession.value = null
    safeLocalStorage('remove', 'rootSession')
    // onAuthStateChanged will update user and account automatically
  }

  // Restore root session on page load if impersonating
  const savedRootSession = safeLocalStorage('get', 'rootSession')
  if (savedRootSession) {
    try {
      rootSession.value = JSON.parse(savedRootSession)
    } catch (e) {
      console.error('Failed to restore root session:', e)
      safeLocalStorage('remove', 'rootSession')
    }
  }

  // Rehydrate store on page reload when Firebase restores session
  onAuthStateChanged(getAuthInstance(), async (currentUser) => {
    if (currentUser) {
      user.value = currentUser
      // Set user context for error reporting
      setUserContext({
        uid: currentUser.uid,
        email: currentUser.email
      })
      // Load account only if not already present
      if (!account.value) {
        try {
          // Detect browser language for account creation
          const browserLang = navigator.language || navigator.userLanguage
          const a = await api.login(currentUser, storedInviteToken.value, browserLang)
          account.value = a
          storedInviteToken.value = null
          // Sync i18n locale with account language preference
          if (a?.lang) {
            i18n.global.locale.value = a.lang
          }
        } catch {
          // If backend login fails, clear local state to avoid inconsistent UI
          user.value = null
          account.value = null
          setUserContext(null)
        }
      }
    } else {
      user.value = null
      account.value = null
      setUserContext(null)
    }
  })
  return {
    user,
    account,
    login,
    logOut,
    refreshToken,
    isLoggedIn,
    setInviteToken,
    getInviteToken,
    inviteToken: storedInviteToken,
    isRoot,
    isImpersonating,
    rootSession,
    startImpersonation,
    exitImpersonation
  }
})
